import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, doc, deleteDoc, writeBatch, onSnapshot, query, orderBy, updateDoc } from 'firebase/firestore';

// Class Type Management Component
export const ClassTypeManagement = ({
  classTypes,
  newClassType,
  setNewClassType,
  selectedIcon,
  setSelectedIcon,
  handleAddClassType,
  handleDeleteClassType
}) => {
  return (
    <div className="add-tag-section">
      <h3>Manage Class Types</h3>
      <input
        type="text"
        placeholder="New class type"
        value={newClassType}
        onChange={(e) => setNewClassType(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && handleAddClassType()}
      />
      <div className="icon-picker">
        <label>Icon:</label>
        <select 
          value={selectedIcon} 
          onChange={(e) => setSelectedIcon(e.target.value)}
          className="icon-select"
        >
          <option value="fa-graduation-cap">Graduation Cap</option>
          <option value="fa-file-lines">Document</option>
          <option value="fa-rocket">Independent Study</option>
          <option value="fa-brain">Brain</option>
          <option value="fa-book">Book</option>
          <option value="fa-toolbox">Tool Box</option>
          <option value="fa-code">Code</option>
          <option value="fa-sprout">Sprout</option>
          <option value="fa-dumbbell">Dumbbell</option>
          <option value="fa-handshake">Handshake</option>
          <option value="fa-earth-americas">Globe</option>
        </select>
      </div>
      <button 
        onClick={handleAddClassType}
        disabled={!newClassType.trim()}
      >
        Add Class Type
      </button>
      <div className="class-type-list">
        {classTypes.map((classType) => (
          <div key={classType.id} className="class-type-item">
            <span className="class-type-name">
              <i className={`fas ${classType.icon || 'fa-graduation-cap'}`}></i>
              {classType.name}
            </span>
            <button
              onClick={() => handleDeleteClassType(classType.id)}
              className="class-type-delete-btn"
              title="Delete class type"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Student-Focused Class Assignment Component
export const StudentFocusedAssignment = ({
  students,
  studentClasses,
  classTypes,
  selectedStudentId,
  setSelectedStudentId,
  studentAssignmentClasses,
  setStudentAssignmentClasses,
  handleAssignClassesToStudent,
  handleRemoveClassFromStudent
}) => {
  const selectedStudent = students.find(s => s.id === selectedStudentId);
  const studentExistingClasses = studentClasses.filter(sc => sc.studentId === selectedStudentId);
  
  // Get available classes (not already assigned to this student)
  // Group classes by name and grade to avoid duplicates
  const uniqueClasses = Object.values(
    studentClasses.reduce((acc, sc) => {
      const key = `${sc.className}-${sc.grade}`;
      if (!acc[key]) {
        acc[key] = {
          id: sc.id,
          className: sc.className,
          grade: sc.grade,
          classTypeId: sc.classTypeId,
          description: sc.description,
          count: 1
        };
      } else {
        acc[key].count++;
      }
      return acc;
    }, {})
  );

  const availableClasses = uniqueClasses
    .map(classItem => {
      const existingClassOfThisName = studentExistingClasses.find(sc => 
        sc.className === classItem.className && sc.grade === classItem.grade
      );
      return {
        ...classItem,
        alreadyAssigned: !!existingClassOfThisName,
        existingClassName: existingClassOfThisName?.className
      };
    })
    .filter(classItem => !classItem.alreadyAssigned); // Only show classes not already assigned

  return (
    <div className="create-post">
      <h3>Assign Classes to Student</h3>
      <p className="section-description">
        Select a student first, then assign classes to them. Only classes not already assigned to the student will be shown.
      </p>
      
      <div className="class-assignment-form">
        <div className="form-row">
          <div className="student-selection-container">
            <label className="student-selection-label">Select Student:</label>
            <select
              value={selectedStudentId}
              onChange={(e) => {
                setSelectedStudentId(e.target.value);
                setStudentAssignmentClasses([]); // Clear class selections when student changes
              }}
              className="student-select-dropdown"
            >
              <option value="">Choose a student...</option>
              {students
                .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
                .map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name || student.id}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {selectedStudent && (
          <>
            <div className="form-row">
              <div className="student-info-section">
                <h4>Assigning classes to: {selectedStudent.name || selectedStudent.id}</h4>
                {studentExistingClasses.length > 0 && (
                  <div className="existing-classes-info">
                    <p><strong>Already assigned classes:</strong></p>
                    <ul className="assigned-classes-list">
                      {studentExistingClasses
                        .sort((a, b) => a.className.localeCompare(b.className))
                        .map(sc => {
                        const classType = classTypes.find(ct => ct.id === sc.classTypeId);
                        return (
                          <li key={sc.id} className="assigned-class-item">
                            <div className="assigned-class-content">
                              <i className={`fas ${classType?.icon || 'fa-graduation-cap'}`}></i>
                              <span className="assigned-class-text">
                                {sc.className} ({sc.grade}th Grade) - {classType?.name}
                              </span>
                            </div>
                            <button
                              className="class-type-delete-btn"
                              onClick={() => {
                                if (window.confirm(`Remove "${sc.className}" from ${selectedStudent.name || selectedStudent.id}?`)) {
                                  handleRemoveClassFromStudent(sc.id);
                                }
                              }}
                              title={`Remove ${sc.className} from ${selectedStudent.name || selectedStudent.id}`}
                            >
                              ×
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="class-selection-container">
                <label className="student-selection-label">Select Classes to Assign:</label>
                <div className="student-checkbox-list checkbox-list-tall">
                  {availableClasses
                    .sort((a, b) => {
                      // Sort by class name first, then by grade
                      const nameCompare = a.className.localeCompare(b.className);
                      if (nameCompare !== 0) return nameCompare;
                      return parseInt(a.grade) - parseInt(b.grade);
                    })
                    .map(classItem => {
                      const classType = classTypes.find(ct => ct.id === classItem.classTypeId);
                      return (
                        <label key={classItem.id} className="student-checkbox-item">
                          <input
                            type="checkbox"
                            checked={studentAssignmentClasses.includes(classItem.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setStudentAssignmentClasses([...studentAssignmentClasses, classItem.id]);
                              } else {
                                setStudentAssignmentClasses(studentAssignmentClasses.filter(id => id !== classItem.id));
                              }
                            }}
                          />
                          <span className="student-checkbox-name">
                            <i className={`fas ${classType?.icon || 'fa-graduation-cap'}`}></i>
                            {classItem.className} ({classItem.grade}th Grade) - {classType?.name}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>
            </div>

            <div className="form-row">
              <button
                className="class-assignment-button"
                onClick={() => handleAssignClassesToStudent(selectedStudentId, studentAssignmentClasses)}
                disabled={studentAssignmentClasses.length === 0}
              >
                Assign {studentAssignmentClasses.length} Class{studentAssignmentClasses.length !== 1 ? 'es' : ''} to {selectedStudent.name || selectedStudent.id}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Class Editing Component
export const ClassEditingSection = ({
  studentClasses,
  classTypes,
  selectedEditClassName,
  setSelectedEditClassName,
  editClassForm,
  setEditClassForm,
  handleUpdateClass
}) => {
  // Group classes by name only to get unique class names
  const uniqueClassNames = Object.values(
    studentClasses.reduce((acc, sc) => {
      const className = sc.className;
      if (!acc[className]) {
        acc[className] = {
          className: className,
          classTypeId: sc.classTypeId,
          description: sc.description,
          grades: [sc.grade],
          totalStudents: 1
        };
      } else {
        if (!acc[className].grades.includes(sc.grade)) {
          acc[className].grades.push(sc.grade);
        }
        acc[className].totalStudents++;
      }
      return acc;
    }, {})
  );

  const selectedClass = uniqueClassNames.find(c => c.className === selectedEditClassName);

  const handleClassSelect = (className) => {
    setSelectedEditClassName(className);
    const classItem = uniqueClassNames.find(c => c.className === className);
    if (classItem) {
      setEditClassForm({
        className: classItem.className,
        description: classItem.description || '',
        classTypeId: classItem.classTypeId,
        grade: '' // Don't pre-select a grade since we're editing across all grades
      });
    }
  };

  return (
    <div className="create-post">
      <h3>Edit Classes</h3>
      <p className="section-description">
        Select a class to edit its details. Changes will apply to ALL instances of this class across ALL grades.
      </p>
      
      <div className="class-assignment-form">
        <div className="form-row">
          <div className="student-selection-container">
            <label className="student-selection-label">Select Class to Edit:</label>
            <select
              value={selectedEditClassName}
              onChange={(e) => handleClassSelect(e.target.value)}
              className="student-select-dropdown"
            >
              <option value="">Choose a class...</option>
              {uniqueClassNames
                .sort((a, b) => a.className.localeCompare(b.className))
                .map(classItem => {
                  const classType = classTypes.find(ct => ct.id === classItem.classTypeId);
                  const gradesText = classItem.grades.sort((a, b) => parseInt(a) - parseInt(b)).join(', ');
                  return (
                    <option key={classItem.className} value={classItem.className}>
                      {classItem.className} - {classType?.name} (Grades: {gradesText}, {classItem.totalStudents} students)
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {selectedClass && (
          <>
            <div className="form-row">
              <div className="class-assignment-input">
                <label>Class Name:</label>
                <input
                  type="text"
                  placeholder="Class name..."
                  value={editClassForm.className}
                  onChange={(e) => setEditClassForm({ ...editClassForm, className: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="class-assignment-input">
                <label>Class Description:</label>
                <textarea
                  placeholder="Class description..."
                  value={editClassForm.description}
                  onChange={(e) => setEditClassForm({ ...editClassForm, description: e.target.value })}
                  className="class-description-input"
                  rows="3"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="class-assignment-input">
                <label>Class Type:</label>
                <select
                  value={editClassForm.classTypeId}
                  onChange={(e) => setEditClassForm({ ...editClassForm, classTypeId: e.target.value })}
                >
                  <option value="">Select class type...</option>
                  {classTypes.map(classType => (
                    <option key={classType.id} value={classType.id}>
                      {classType.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="class-info-section">
                <h4>Current Class Information:</h4>
                <p><strong>Class:</strong> {selectedClass.className}</p>
                <p><strong>Grades:</strong> {selectedClass.grades.sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}</p>
                <p><strong>Total Students:</strong> {selectedClass.totalStudents}</p>
                <p><strong>Description:</strong> {selectedClass.description || 'No description'}</p>
              </div>
            </div>

            <div className="form-row">
              <button
                className="class-assignment-button"
                onClick={() => handleUpdateClass(selectedEditClassName, editClassForm)}
                disabled={!editClassForm.className.trim() || !editClassForm.classTypeId}
              >
                Update Class Across All Grades
              </button>
              <button
                className="proposal-cancel-btn"
                onClick={() => {
                  setSelectedEditClassName('');
                  setEditClassForm({ className: '', description: '', classTypeId: '', grade: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Delete Class Entirely Component
export const DeleteClassEntirely = ({
  studentClasses,
  classTypes,
  selectedClassForDeletion,
  setSelectedClassForDeletion,
  handleDeleteClassEntirely
}) => {
  // Group classes by name only to get unique class names
  const uniqueClassNames = Object.values(
    studentClasses.reduce((acc, sc) => {
      const className = sc.className;
      if (!acc[className]) {
        acc[className] = {
          className: className,
          classTypeId: sc.classTypeId,
          description: sc.description,
          grades: [sc.grade],
          totalStudents: 1
        };
      } else {
        if (!acc[className].grades.includes(sc.grade)) {
          acc[className].grades.push(sc.grade);
        }
        acc[className].totalStudents++;
      }
      return acc;
    }, {})
  );

  const selectedClass = uniqueClassNames.find(c => c.className === selectedClassForDeletion);

  return (
    <div className="create-post">
      <h3>Delete Class Entirely</h3>
      <p className="section-description">
        <strong>⚠️ WARNING:</strong> This will permanently delete the class from ALL students across ALL grades. This action cannot be undone.
      </p>
      
      <div className="class-assignment-form">
        <div className="form-row">
          <div className="student-selection-container">
            <label className="student-selection-label">Select Class to Delete:</label>
            <select
              value={selectedClassForDeletion}
              onChange={(e) => setSelectedClassForDeletion(e.target.value)}
              className="student-select-dropdown"
            >
              <option value="">Choose a class...</option>
              {uniqueClassNames
                .sort((a, b) => a.className.localeCompare(b.className))
                .map(classItem => {
                  const classType = classTypes.find(ct => ct.id === classItem.classTypeId);
                  const gradesText = classItem.grades.sort((a, b) => parseInt(a) - parseInt(b)).join(', ');
                  return (
                    <option key={classItem.className} value={classItem.className}>
                      {classItem.className} - {classType?.name} (Grades: {gradesText}, {classItem.totalStudents} students)
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {selectedClass && (
          <>
            <div className="form-row">
              <div className="class-info-section">
                <h4>⚠️ Class Deletion Confirmation:</h4>
                <p><strong>Class Name:</strong> {selectedClass.className}</p>
                <p><strong>Grades:</strong> {selectedClass.grades.sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}</p>
                <p><strong>Total Students Affected:</strong> {selectedClass.totalStudents}</p>
                <p><strong>Description:</strong> {selectedClass.description || 'No description'}</p>
                <p style={{ color: 'var(--color-error)', fontWeight: 'bold', marginTop: 'var(--spacing-md)' }}>
                  This will permanently remove this class from ALL students' transcripts!
                </p>
              </div>
            </div>

            <div className="form-row">
              <button
                className="proposal-reject-btn"
                onClick={() => handleDeleteClassEntirely(selectedClassForDeletion)}
                disabled={!selectedClassForDeletion}
                style={{ backgroundColor: 'var(--color-error)', borderColor: 'var(--color-error)' }}
              >
                🗑️ DELETE CLASS PERMANENTLY
              </button>
              <button
                className="proposal-cancel-btn"
                onClick={() => setSelectedClassForDeletion('')}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Class-Focused Assignment Component
export const ClassFocusedAssignment = ({
  students,
  studentClasses,
  classTypes,
  selectedClassId,
  setSelectedClassId,
  classAssignmentStudents,
  setClassAssignmentStudents,
  handleAssignStudentsToClass,
  handleRemoveClassFromStudent
}) => {
  const selectedClass = studentClasses.find(sc => sc.id === selectedClassId);
  
  // Get students already assigned to this specific class
  const assignedStudents = studentClasses
    .filter(sc => sc.className === selectedClass?.className && sc.grade === selectedClass?.grade)
    .map(sc => ({
      ...sc,
      student: students.find(s => s.id === sc.studentId)
    }));

  // Get available students (not already assigned to this specific class)
  const availableStudents = students.map(student => {
    const existingAssignment = studentClasses.find(sc => 
      sc.studentId === student.id && 
      sc.className === selectedClass?.className && 
      sc.grade === selectedClass?.grade
    );
    return {
      ...student,
      alreadyAssigned: !!existingAssignment,
      existingClassName: existingAssignment?.className
    };
  });

  // Group existing classes by name and grade for selection
  const uniqueClasses = Object.values(
    studentClasses.reduce((acc, sc) => {
      const key = `${sc.className}-${sc.grade}`;
      if (!acc[key]) {
        acc[key] = {
          id: sc.id,
          className: sc.className,
          grade: sc.grade,
          classTypeId: sc.classTypeId,
          description: sc.description,
          count: 1
        };
      } else {
        acc[key].count++;
      }
      return acc;
    }, {})
  );

  return (
    <div className="create-post">
      <h3>Assign Students to Class</h3>
      <p className="section-description">
        Select an existing class first, then assign students to it. Students cannot be assigned to the same class twice.
      </p>
      
      <div className="class-assignment-form">
        <div className="form-row">
          <div className="student-selection-container">
            <label className="student-selection-label">Select Class:</label>
            <select
              value={selectedClassId}
              onChange={(e) => {
                setSelectedClassId(e.target.value);
                setClassAssignmentStudents([]); // Clear student selections when class changes
              }}
              className="student-select-dropdown"
            >
              <option value="">Choose a class...</option>
              {uniqueClasses
                .sort((a, b) => {
                  // Sort by class name first, then by grade
                  const nameCompare = a.className.localeCompare(b.className);
                  if (nameCompare !== 0) return nameCompare;
                  return parseInt(a.grade) - parseInt(b.grade);
                })
                .map(classItem => {
                  const classType = classTypes.find(ct => ct.id === classItem.classTypeId);
                  return (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.className} ({classItem.grade}th Grade) - {classType?.name} ({classItem.count} students)
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {selectedClass && (
          <>
            <div className="form-row">
              <div className="student-info-section">
                <h4>Assigning students to: {selectedClass.className} ({selectedClass.grade}th Grade)</h4>
                {selectedClass.description && (
                  <p className="class-description-text">
                    {selectedClass.description}
                  </p>
                )}
                {assignedStudents.length > 0 && (
                  <div className="existing-classes-info">
                    <p><strong>Students already assigned to this class:</strong></p>
                    <ul className="assigned-classes-list">
                      {assignedStudents
                        .sort((a, b) => (a.student?.name || a.studentId).localeCompare(b.student?.name || b.studentId))
                        .map(sc => (
                        <li key={sc.id} className="assigned-class-item">
                          <div className="assigned-class-content">
                            <span className="assigned-class-text">
                              {sc.student?.name || sc.studentId}
                            </span>
                          </div>
                          <button
                            className="class-type-delete-btn"
                            onClick={() => {
                              if (window.confirm(`Remove "${sc.student?.name || sc.studentId}" from ${selectedClass.className}?`)) {
                                handleRemoveClassFromStudent(sc.id);
                              }
                            }}
                            title={`Remove ${sc.student?.name || sc.studentId} from ${selectedClass.className}`}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="class-selection-container">
                <label className="student-selection-label">Select Students to Assign:</label>
                <div className="student-checkbox-list checkbox-list-tall">
                  {availableStudents
                    .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
                    .map(student => (
                      <label key={student.id} className="student-checkbox-item">
                        <input
                          type="checkbox"
                          checked={classAssignmentStudents.includes(student.id)}
                          disabled={student.alreadyAssigned}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setClassAssignmentStudents([...classAssignmentStudents, student.id]);
                            } else {
                              setClassAssignmentStudents(classAssignmentStudents.filter(id => id !== student.id));
                            }
                          }}
                        />
                        <span className="student-checkbox-name">
                          {student.name || student.id}
                          {student.alreadyAssigned && (
                            <span className="already-assigned-text">
                               (Already assigned to this class)
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            </div>

            <div className="form-row">
              <button
                className="class-assignment-button"
                onClick={() => handleAssignStudentsToClass(selectedClassId, classAssignmentStudents)}
                disabled={classAssignmentStudents.length === 0}
              >
                Assign {classAssignmentStudents.length} Student{classAssignmentStudents.length !== 1 ? 's' : ''} to {selectedClass.className}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Flexible Class Assignment Form Component
export const FlexibleClassAssignmentForm = ({
  newClass,
  setNewClass,
  students,
  classTypes,
  handleAddFlexibleClass
}) => {
  const [studentGradeAssignments, setStudentGradeAssignments] = useState({});

  const handleStudentSelection = (studentId, isSelected) => {
    if (isSelected) {
      setStudentGradeAssignments(prev => ({
        ...prev,
        [studentId]: prev[studentId] || { grade: '', studentId }
      }));
    } else {
      setStudentGradeAssignments(prev => {
        const updated = { ...prev };
        delete updated[studentId];
        return updated;
      });
    }
  };

  const handleGradeChange = (studentId, grade) => {
    setStudentGradeAssignments(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], grade, studentId }
    }));
  };

  const handleSubmit = () => {
    const assignments = Object.values(studentGradeAssignments).filter(
      assignment => assignment.grade && assignment.studentId
    );
    
    if (assignments.length === 0) {
      alert('Please select at least one student and assign them a grade.');
      return;
    }

    handleAddFlexibleClass(assignments);
  };

  return (
    <div className="create-post">
      <h3>Create Flexible Class Assignment</h3>
      <p className="section-description">
        Create a class and assign it to students in different grades. For example, assign Calculus to Timmy in 9th grade and John in 10th grade.
      </p>
      
      <div className="class-assignment-form">
        <div className="form-row">
          <div className="class-assignment-input">
            <label>Class Name:</label>
            <input
              type="text"
              placeholder="e.g., Calculus, AP Computer Science, Algebra 2..."
              value={newClass.className}
              onChange={(e) => setNewClass({ ...newClass, className: e.target.value })}
            />
          </div>
          <div className="class-assignment-input">
            <label>Class Description (Optional):</label>
            <textarea
              placeholder="Describe what this class covers..."
              value={newClass.description}
              onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
              className="class-description-input"
              rows="3"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="class-assignment-input">
            <label>Class Type:</label>
            <select
              value={newClass.classTypeId}
              onChange={(e) => setNewClass({ ...newClass, classTypeId: e.target.value })}
            >
              <option value="">Select class type...</option>
              {classTypes.map(classType => (
                <option key={classType.id} value={classType.id}>
                  {classType.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="student-selection-container">
            <label className="student-selection-label">Select Students and Assign Grades:</label>
            <div className="flexible-assignment-list">
              {students.map(student => {
                const isSelected = studentGradeAssignments[student.id];
                const assignedGrade = isSelected?.grade || '';
                
                return (
                  <div key={student.id} className="flexible-assignment-item">
                    <label className="student-checkbox-item">
                      <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={(e) => handleStudentSelection(student.id, e.target.checked)}
                      />
                      <span className="student-checkbox-name">{student.name || student.id}</span>
                    </label>
                    
                    <div className={`grade-assignment ${!isSelected ? 'hidden' : ''}`}>
                      <select
                        value={assignedGrade}
                        onChange={(e) => handleGradeChange(student.id, e.target.value)}
                        disabled={!isSelected}
                      >
                        <option value="">Select grade...</option>
                        <option value="8">8th Grade</option>
                        <option value="9">9th Grade</option>
                        <option value="10">10th Grade</option>
                        <option value="11">11th Grade</option>
                        <option value="12">12th Grade</option>
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="form-row">
          <button
            className="class-assignment-button"
            onClick={handleSubmit}
            disabled={!newClass.className.trim() || !newClass.classTypeId || Object.values(studentGradeAssignments).filter(a => a.grade).length === 0}
          >
            Assign Class to {Object.values(studentGradeAssignments).filter(a => a.grade).length} Student{Object.values(studentGradeAssignments).filter(a => a.grade).length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};



// Helper function to categorize class types
const categorizeClassType = (classTypeName) => {
  if (!classTypeName) return 'Experiential Studies';
  
  const coreClasses = ['Idea Lab', 'Literature Circle', 'Tool Kit', 'Tech Studio', 'Community Forum'];
  const advancedStudies = ['Independent Study', 'College Credit', 'AP Preparation'];
  const experientialStudies = ['Work Experience', 'Service Experience', 'Elective'];
  
  if (coreClasses.includes(classTypeName)) {
    return 'Core Classes';
  } else if (advancedStudies.includes(classTypeName)) {
    return 'Advanced Studies';
  } else if (experientialStudies.includes(classTypeName)) {
    return 'Experiential Studies';
  }
  
  // Default to Experiential Studies for unknown types
  return 'Experiential Studies';
};

// Helper function to get category order
const getCategoryOrder = (category) => {
  const order = {
    'Core Classes': 1,
    'Advanced Studies': 2,
    'Experiential Studies': 3
  };
  return order[category] || 4;
};

// Helper function to get class type order within category
const getClassTypeOrder = (classTypeName) => {
  const coreOrder = {
    'Idea Lab': 1,
    'Literature Circle': 2,
    'Tool Kit': 3,
    'Tech Studio': 4,
    'Community Forum': 5
  };
  
  const advancedOrder = {
    'Independent Study': 1,
    'College Credit': 2,
    'AP Preparation': 3
  };
  
  const experientialOrder = {
    'Work Experience': 1,
    'Service Experience': 2,
    'Elective': 3
  };
  
  if (coreOrder[classTypeName]) return coreOrder[classTypeName];
  if (advancedOrder[classTypeName]) return advancedOrder[classTypeName];
  if (experientialOrder[classTypeName]) return experientialOrder[classTypeName];
  
  return 999; // Unknown types go last
};

// Student Classes Display Component
export const StudentClassesDisplay = ({
  studentClasses,
  classTypes,
  currentUser,
  selectedStudentUid
}) => {
  const targetStudentId = selectedStudentUid || currentUser?.uid;
  const myClasses = studentClasses.filter(sc => sc.studentId === targetStudentId);
  // Classes are sorted by grade later when grouping by grade
  
  if (myClasses.length === 0) {
    return (
      <div className="student-classes-empty">
        No classes assigned yet
      </div>
    );
  }

  // Group classes by grade, then by category, then by class type
  const classesByGrade = myClasses.reduce((groups, classItem) => {
    const grade = classItem.grade || 'Unknown';
    const classType = classTypes.find(ct => ct.id === classItem.classTypeId);
    const category = categorizeClassType(classType?.name);
    
    if (!groups[grade]) {
      groups[grade] = {};
    }
    if (!groups[grade][category]) {
      groups[grade][category] = {};
    }
    if (!groups[grade][category][classType?.name || 'Unknown']) {
      groups[grade][category][classType?.name || 'Unknown'] = [];
    }
    
    groups[grade][category][classType?.name || 'Unknown'].push(classItem);
    return groups;
  }, {});

  // Sort classes within each class type alphabetically by class name
  Object.keys(classesByGrade).forEach(grade => {
    Object.keys(classesByGrade[grade]).forEach(category => {
      Object.keys(classesByGrade[grade][category]).forEach(classTypeName => {
        classesByGrade[grade][category][classTypeName].sort((a, b) => {
          return a.className.localeCompare(b.className, undefined, { numeric: true });
        });
      });
    });
  });

  // Sort grades in ascending order (8th grade first), then Portfolio Defense, Unknown last
  const sortedGrades = Object.keys(classesByGrade).sort((a, b) => {
    // Handle 'Unknown' grade by putting it last
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    // Portfolio Defense goes after all numeric grades
    if (a === 'Portfolio Defense') return 1;
    if (b === 'Portfolio Defense') return -1;
    // Numeric grades in ascending order (8th -> 12th)
    const gradeA = parseInt(a) || 0;
    const gradeB = parseInt(b) || 0;
    return gradeA - gradeB;
  });

  return (
    <div className="student-classes-container">
      <h3 className="transcript-title">
        Classes
      </h3>
      <div className="student-classes-list">
        {sortedGrades.map((grade) => (
          <div key={grade} className="grade-section">
            <h4 className="grade-section-header">
              {grade}th Grade
            </h4>
            
            {/* Sort categories by order */}
            {Object.keys(classesByGrade[grade])
              .sort((a, b) => getCategoryOrder(a) - getCategoryOrder(b))
              .map((category) => (
                <div key={category} className="category-section">
                  <h5 className="category-section-header">
                    {category}
                  </h5>
                  
                  {/* Sort class types within category by order */}
                  {Object.keys(classesByGrade[grade][category])
                    .sort((a, b) => getClassTypeOrder(a) - getClassTypeOrder(b))
                    .map((classTypeName) => (
                      <div key={classTypeName} className="class-type-section">
                        <div className="class-type-classes-list">
                          {classesByGrade[grade][category][classTypeName].map((classItem, index) => {
                            const classType = classTypes.find(ct => ct.id === classItem.classTypeId);
                            return (
                              <div key={index} className="student-class-item">
                                <i className={`fa-solid ${classType?.icon || 'fa-graduation-cap'} student-class-icon`}></i>
                                <div className="student-class-details">
                                  <span className="student-class-name">
                                    {classItem.className}
                                  </span>
                                  {classItem.description && (
                                    <span className="student-class-description">
                                      {classItem.description}
                                    </span>
                                  )}
                                </div>
                                {classType && (
                                  <span className="student-class-type">
                                    {classType.name}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Class Proposal Form Component for Students
export const ClassProposalForm = ({
  newClassProposal,
  setNewClassProposal,
  classTypes,
  currentUser,
  userProfile,
  handleAddClassProposal
}) => {
  // Filter to only show Independent Study and College Credit class types
  const allowedClassTypes = classTypes.filter(classType => 
    classType.name === 'Independent Study' || classType.name === 'College Credit'
  );

  return (
    <div className="create-post">
      <h2>Propose a New Class</h2>
      <p className="transcript-description">
        Propose an Independent Study or College Credit class. Once approved, it will appear on your class list and transcript.
      </p>
      
      <div className="form-group">
        <label>Class Name: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <input
          type="text"
          placeholder="e.g., Advanced Calculus, AP Psychology"
          value={newClassProposal.className}
          onChange={(e) => setNewClassProposal({ ...newClassProposal, className: e.target.value })}
          className="post-title-input"
        />
      </div>

      <div className="form-group" style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
        <div style={{ flex: 1 }}>
          <label>Class Type: <span style={{color: 'var(--color-error)'}}>*</span></label>
          <select
            value={newClassProposal.classTypeId}
            onChange={(e) => setNewClassProposal({ ...newClassProposal, classTypeId: e.target.value })}
            className="post-title-input"
          >
            <option value="">Select class type...</option>
            {allowedClassTypes.map(classType => (
              <option key={classType.id} value={classType.id}>
                {classType.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label>Grade Level: <span style={{color: 'var(--color-error)'}}>*</span></label>
          <select
            value={newClassProposal.grade}
            onChange={(e) => setNewClassProposal({ ...newClassProposal, grade: e.target.value })}
            className="post-title-input"
          >
            <option value="">Select grade...</option>
            <option value="8">8th Grade</option>
            <option value="9">9th Grade</option>
            <option value="10">10th Grade</option>
            <option value="11">11th Grade</option>
            <option value="12">12th Grade</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Class Description (Optional)</label>
        <textarea
          placeholder="Describe what this class will cover and what students will learn..."
          value={newClassProposal.description}
          onChange={(e) => setNewClassProposal({ ...newClassProposal, description: e.target.value })}
          className="post-description-input"
          rows="4"
        />
      </div>

      <button 
        onClick={handleAddClassProposal} 
        className="class-assignment-button"
        disabled={!newClassProposal.className.trim() || !newClassProposal.classTypeId || !newClassProposal.grade}
      >
        Propose Class
      </button>
    </div>
  );
};

// Class Proposal Management Component for Teachers
export const ClassProposalManagement = ({
  classProposals,
  classTypes,
  editingProposal,
  editProposalForm,
  setEditProposalForm,
  handleApproveProposal,
  handleRejectProposal,
  handleEditProposal,
  handleSaveProposalEdit,
  handleCancelProposalEdit
}) => {
  if (classProposals.length === 0) {
    return (
      <div className="create-post">
        <h3>Class Proposals</h3>
        <p className="no-posts no-posts-padding">No class proposals pending review.</p>
      </div>
    );
  }

  return (
    <div className="create-post">
      <h3>Class Proposals</h3>
      <p className="section-description">
        Review and approve or reject class proposals from students. You can edit the title and description before approving.
      </p>
      
      <div className="proposal-list">
        {classProposals.map(proposal => {
          const classType = classTypes.find(ct => ct.id === proposal.classTypeId);
          const isEditing = editingProposal === proposal.id;
          
          return (
            <div key={proposal.id} className="proposal-item">
              <div className="proposal-header">
                <div className="proposal-title">
                  <i className={`fas ${classType?.icon || 'fa-graduation-cap'}`}></i>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editProposalForm.className}
                      onChange={(e) => setEditProposalForm({ ...editProposalForm, className: e.target.value })}
                      className="proposal-edit-input"
                    />
                  ) : (
                    proposal.className
                  )}
                </div>
                <div className="proposal-meta">
                  {proposal.grade}th Grade · {classType?.name || 'Unknown Type'}
                </div>
              </div>
              
              <div className="proposal-description">
                {isEditing ? (
                  <textarea
                    value={editProposalForm.description}
                    onChange={(e) => setEditProposalForm({ ...editProposalForm, description: e.target.value })}
                    className="proposal-edit-textarea"
                    rows="3"
                  />
                ) : (
                  proposal.description || <em>No description provided</em>
                )}
              </div>
              
              <div className="proposal-footer">
                <div className="proposal-student">
                  Proposed by: {proposal.studentName || proposal.studentId}
                </div>
                <div className="proposal-actions">
                  {isEditing ? (
                    <>
                      <button
                        className="proposal-save-btn"
                        onClick={handleSaveProposalEdit}
                      >
                        Save
                      </button>
                      <button
                        className="proposal-cancel-btn"
                        onClick={handleCancelProposalEdit}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="proposal-edit-btn"
                        onClick={() => handleEditProposal(proposal.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="proposal-approve-btn"
                        onClick={() => handleApproveProposal(proposal.id)}
                      >
                        Approve
                      </button>
                      <button
                        className="proposal-reject-btn"
                        onClick={() => handleRejectProposal(proposal.id)}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Class Management Hook
export const useClassManagement = ({ currentUser, userProfile }) => {
  const [studentClasses, setStudentClasses] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [classProposals, setClassProposals] = useState([]);
  const [newClass, setNewClass] = useState({ className: '', studentIds: [], classTypeId: '', description: '', grade: '' });
  const [newClassProposal, setNewClassProposal] = useState({ className: '', classTypeId: '', description: '', grade: '' });
  const [editingProposal, setEditingProposal] = useState(null);
  const [editProposalForm, setEditProposalForm] = useState({ className: '', description: '' });
  const [newClassType, setNewClassType] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('fa-graduation-cap');
  
  // New workflow states
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentAssignmentClasses, setStudentAssignmentClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [classAssignmentStudents, setClassAssignmentStudents] = useState([]);
  
  // Class editing states
  const [selectedEditClassName, setSelectedEditClassName] = useState('');
  const [editClassForm, setEditClassForm] = useState({ className: '', description: '', classTypeId: '', grade: '' });
  
  // Class deletion states
  const [selectedClassForDeletion, setSelectedClassForDeletion] = useState('');

  // Listen for Student Classes, Class Types, and Class Proposals
  useEffect(() => {
    if (!currentUser) {
      setStudentClasses([]);
      setClassTypes([]);
      setClassProposals([]);
      return;
    }

    // Listen for Student Classes
    const qStudentClasses = query(collection(db, 'studentClasses'));
    const unsubscribeStudentClasses = onSnapshot(qStudentClasses, (snapshot) => {
      const classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStudentClasses(classesData);
    }, (error) => console.error("Error fetching student classes:", error));

    // Listen for Class Types
    const qClassTypes = query(collection(db, 'classTypes'), orderBy('name'));
    const unsubscribeClassTypes = onSnapshot(qClassTypes, (snapshot) => {
      const classTypesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClassTypes(classTypesData);
    }, (error) => console.error("Error fetching class types:", error));

    // Listen for Class Proposals
    const qClassProposals = query(collection(db, 'classProposals'), orderBy('timestamp', 'desc'));
    const unsubscribeClassProposals = onSnapshot(qClassProposals, (snapshot) => {
      const proposalsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClassProposals(proposalsData);
    }, (error) => console.error("Error fetching class proposals:", error));

    // Cleanup function
    return () => {
      unsubscribeStudentClasses();
      unsubscribeClassTypes();
      unsubscribeClassProposals();
    };
  }, [currentUser]);

  const handleAddClassType = async () => {
    if (!newClassType.trim() || !currentUser) return;

    try {
      await addDoc(collection(db, 'classTypes'), {
        name: newClassType.trim(),
        description: `Custom class type: ${newClassType.trim()}`,
        icon: selectedIcon,
        createdBy: currentUser.uid,
        timestamp: new Date().toISOString(),
      });

      // Clear the form
      setNewClassType('');
      setSelectedIcon('fa-graduation-cap');
    } catch (error) {
      console.error("Error adding class type:", error.message);
      alert('Failed to add class type. Please try again.');
    }
  };

  const handleDeleteClassType = async (classTypeId) => {
    if (!currentUser) return;

    try {
      await deleteDoc(doc(db, 'classTypes', classTypeId));
    } catch (error) {
      console.error("Error deleting class type:", error.message);
      alert('Failed to delete class type. Please try again.');
    }
  };

  const handleAddFlexibleClass = async (studentGradeAssignments) => {
    if (!newClass.className.trim() || !newClass.classTypeId || !currentUser) return;

    try {
      // Create a class assignment for each student-grade combination
      const classAssignments = studentGradeAssignments.map(assignment => ({
        className: newClass.className.trim(),
        studentId: assignment.studentId,
        classTypeId: newClass.classTypeId,
        description: newClass.description.trim(),
        grade: assignment.grade,
        teacherId: currentUser.uid,
      }));

      // Add all class assignments to Firebase
      const batch = writeBatch(db);
      classAssignments.forEach(classAssignment => {
        const docRef = doc(collection(db, 'studentClasses'));
        batch.set(docRef, classAssignment);
      });
      await batch.commit();

      // Clear the form
      setNewClass({ className: '', studentIds: [], classTypeId: '', description: '', grade: '' });
      
      alert(`Successfully assigned "${newClass.className}" to ${classAssignments.length} student${classAssignments.length !== 1 ? 's' : ''}!`);
    } catch (error) {
      console.error("Error adding flexible class:", error.message);
      alert('Failed to add class. Please try again.');
    }
  };


  // New workflow handlers
  const handleAssignClassesToStudent = async (studentId, classIds) => {
    if (!studentId || classIds.length === 0 || !currentUser) {
      alert('Please select a student and at least one class.');
      return;
    }

    try {
      const assignments = [];
      
      classIds.forEach(classId => {
        // Get the class details
        const classToAssign = studentClasses.find(sc => sc.id === classId);
        if (!classToAssign) return;

        // Check if student already has this specific class assigned
          const existingAssignment = studentClasses.find(sc => 
            sc.studentId === studentId && 
          sc.className === classToAssign.className && 
          sc.grade === classToAssign.grade
          );
          
          if (!existingAssignment) {
            assignments.push({
            className: classToAssign.className,
              studentId: studentId,
            classTypeId: classToAssign.classTypeId,
            description: classToAssign.description,
            grade: classToAssign.grade,
            teacherId: currentUser.uid,
            });
          }
      });

      if (assignments.length === 0) {
        alert('Student already has all selected classes assigned.');
        return;
      }

      // Add all assignments to Firebase
      const batch = writeBatch(db);
      assignments.forEach(assignment => {
        const docRef = doc(collection(db, 'studentClasses'));
        batch.set(docRef, assignment);
      });
      await batch.commit();

      // Clear the selections
      setStudentAssignmentClasses([]);
      // alert(`Successfully assigned ${assignments.length} class${assignments.length !== 1 ? 'es' : ''} to student!`);
    } catch (error) {
      console.error("Error assigning classes to student:", error.message);
      alert('Failed to assign classes. Please try again.');
    }
  };

  const handleAssignStudentsToClass = async (classId, studentIds) => {
    if (!classId || studentIds.length === 0 || !currentUser) {
      alert('Please select a class and at least one student.');
      return;
    }

    try {
      // Get the selected class details
      const selectedClass = studentClasses.find(sc => sc.id === classId);
      if (!selectedClass) {
        alert('Selected class not found.');
        return;
      }

      const assignments = [];
      
      studentIds.forEach(studentId => {
        // Check if student already has this specific class assigned
        const existingAssignment = studentClasses.find(sc => 
          sc.studentId === studentId && 
          sc.className === selectedClass.className && 
          sc.grade === selectedClass.grade
        );
        
        if (!existingAssignment) {
          assignments.push({
            className: selectedClass.className,
            studentId: studentId,
            classTypeId: selectedClass.classTypeId,
            description: selectedClass.description,
            grade: selectedClass.grade,
            teacherId: currentUser.uid,
          });
        }
      });

      if (assignments.length === 0) {
        alert('All selected students already have this class assigned.');
        return;
      }

      // Add all assignments to Firebase
      const batch = writeBatch(db);
      assignments.forEach(assignment => {
        const docRef = doc(collection(db, 'studentClasses'));
        batch.set(docRef, assignment);
      });
      await batch.commit();

      // Clear the selections
      setClassAssignmentStudents([]);
      // alert(`Successfully assigned ${assignments.length} student${assignments.length !== 1 ? 's' : ''} to ${selectedClass.className}!`);
    } catch (error) {
      console.error("Error assigning students to class:", error.message);
      alert('Failed to assign students. Please try again.');
    }
  };

  // Class Proposal Handlers
  const handleAddClassProposal = async () => {
    if (!newClassProposal.className.trim() || !newClassProposal.classTypeId || !newClassProposal.grade || !currentUser) return;

    try {
      await addDoc(collection(db, 'classProposals'), {
        className: newClassProposal.className.trim(),
        classTypeId: newClassProposal.classTypeId,
        description: newClassProposal.description.trim(),
        grade: newClassProposal.grade,
        studentId: currentUser.uid,
        studentName: userProfile?.name || 'Student',
        status: 'pending',
        timestamp: new Date().toISOString(),
      });

      // Clear the form
      setNewClassProposal({ className: '', classTypeId: '', description: '', grade: '' });
      alert('Class proposal submitted successfully! Teachers will review it.');
    } catch (error) {
      console.error("Error adding class proposal:", error.message);
      alert('Failed to submit class proposal. Please try again.');
    }
  };

  const handleApproveProposal = async (proposalId) => {
    if (!currentUser) return;

    try {
      const proposal = classProposals.find(p => p.id === proposalId);
      if (!proposal) {
        alert('Proposal not found.');
        return;
      }

      // Create a class assignment for the proposing student
      const classAssignment = {
        className: proposal.className,
        studentId: proposal.studentId,
        classTypeId: proposal.classTypeId,
        description: proposal.description,
        grade: proposal.grade,
        teacherId: currentUser.uid,
      };

      // Add the class assignment to Firebase
      await addDoc(collection(db, 'studentClasses'), classAssignment);

      // Delete the proposal from the proposals collection
      await deleteDoc(doc(db, 'classProposals', proposalId));

      // alert(`Class "${proposal.className}" has been approved and assigned to ${proposal.studentName}!`);
    } catch (error) {
      console.error("Error approving proposal:", error.message);
      alert('Failed to approve proposal. Please try again.');
    }
  };

  const handleRejectProposal = async (proposalId) => {
    if (!currentUser) return;

    try {
      // Delete the proposal from the proposals collection
      await deleteDoc(doc(db, 'classProposals', proposalId));

      // alert('Class proposal has been rejected and removed.');
    } catch (error) {
      console.error("Error rejecting proposal:", error.message);
      alert('Failed to reject proposal. Please try again.');
    }
  };

  const handleEditProposal = (proposalId) => {
    const proposal = classProposals.find(p => p.id === proposalId);
    if (proposal) {
      setEditingProposal(proposalId);
      setEditProposalForm({
        className: proposal.className,
        description: proposal.description || ''
      });
    }
  };

  const handleSaveProposalEdit = async () => {
    if (!editingProposal || !currentUser) return;

    try {
      await updateDoc(doc(db, 'classProposals', editingProposal), {
        className: editProposalForm.className.trim(),
        description: editProposalForm.description.trim()
      });

      setEditingProposal(null);
      setEditProposalForm({ className: '', description: '' });
      // alert('Proposal updated successfully!');
    } catch (error) {
      console.error("Error updating proposal:", error.message);
      alert('Failed to update proposal. Please try again.');
    }
  };

  const handleCancelProposalEdit = () => {
    setEditingProposal(null);
    setEditProposalForm({ className: '', description: '' });
  };

  // Class Update Handler
  const handleUpdateClass = async (className, updatedClassData) => {
    if (!currentUser || !className) return;

    try {
      // Find all instances of this class name across all grades
      const classInstances = studentClasses.filter(sc => 
        sc.className === className
      );

      if (classInstances.length === 0) {
        alert('No instances of this class found.');
        return;
      }

      // Update all instances of this class
      const batch = writeBatch(db);
      classInstances.forEach(classInstance => {
        const docRef = doc(db, 'studentClasses', classInstance.id);
        batch.update(docRef, {
          className: updatedClassData.className.trim(),
          description: updatedClassData.description.trim(),
          classTypeId: updatedClassData.classTypeId
          // Note: We don't update the grade - that stays the same for each student
        });
      });
      
      await batch.commit();

      // Clear the form
      setSelectedEditClassName('');
      setEditClassForm({ className: '', description: '', classTypeId: '', grade: '' });
      
      alert(`Successfully updated "${updatedClassData.className}" across ${classInstances.length} instance${classInstances.length !== 1 ? 's' : ''} in all grades!`);
    } catch (error) {
      console.error("Error updating class:", error.message);
      alert('Failed to update class. Please try again.');
    }
  };

  // Remove Class from Student Handler
  const handleRemoveClassFromStudent = async (classId) => {
    if (!currentUser || !classId) return;

    try {
      // Find the class to get student info for confirmation
      const classToRemove = studentClasses.find(sc => sc.id === classId);
      if (!classToRemove) {
        alert('Class not found.');
        return;
      }

      // Delete the specific class assignment
      await deleteDoc(doc(db, 'studentClasses', classId));
      
      alert(`Successfully removed "${classToRemove.className}" from student!`);
    } catch (error) {
      console.error("Error removing class from student:", error.message);
      alert('Failed to remove class from student. Please try again.');
    }
  };

  // Delete Class Entirely Handler
  const handleDeleteClassEntirely = async (className) => {
    if (!currentUser || !className) return;

    try {
      // Find all instances of this class name across all grades
      const classInstances = studentClasses.filter(sc => 
        sc.className === className
      );

      if (classInstances.length === 0) {
        alert('No instances of this class found.');
        return;
      }

      // Confirm deletion
      const confirmed = window.confirm(
        `Are you sure you want to permanently delete "${className}"?\n\n` +
        `This will remove the class from ${classInstances.length} student${classInstances.length !== 1 ? 's' : ''} across all grades.\n\n` +
        `This action cannot be undone!`
      );

      if (!confirmed) return;

      // Delete all instances of this class
      const batch = writeBatch(db);
      classInstances.forEach(classInstance => {
        const docRef = doc(db, 'studentClasses', classInstance.id);
        batch.delete(docRef);
      });
      
      await batch.commit();

      // Clear the form
      setSelectedClassForDeletion('');
      
      alert(`Successfully deleted "${className}" from all ${classInstances.length} student${classInstances.length !== 1 ? 's' : ''}!`);
    } catch (error) {
      console.error("Error deleting class:", error.message);
      alert('Failed to delete class. Please try again.');
    }
  };

  return {
    studentClasses,
    setStudentClasses,
    classTypes,
    setClassTypes,
    classProposals,
    setClassProposals,
    newClass,
    setNewClass,
    newClassProposal,
    setNewClassProposal,
    editingProposal,
    editProposalForm,
    setEditProposalForm,
    newClassType,
    setNewClassType,
    selectedIcon,
    setSelectedIcon,
    // New workflow states
    selectedStudentId,
    setSelectedStudentId,
    studentAssignmentClasses,
    setStudentAssignmentClasses,
    selectedClassId,
    setSelectedClassId,
    classAssignmentStudents,
    setClassAssignmentStudents,
    // Class editing states
    selectedEditClassName,
    setSelectedEditClassName,
    editClassForm,
    setEditClassForm,
    // Class deletion states
    selectedClassForDeletion,
    setSelectedClassForDeletion,
    // Handlers
    handleAddClassType,
    handleDeleteClassType,
    handleAddFlexibleClass,
    handleAssignClassesToStudent,
    handleAssignStudentsToClass,
    handleUpdateClass,
    handleRemoveClassFromStudent,
    handleDeleteClassEntirely,
    // Class Proposal Handlers
    handleAddClassProposal,
    handleApproveProposal,
    handleRejectProposal,
    handleEditProposal,
    handleSaveProposalEdit,
    handleCancelProposalEdit
  };
};

const classManagementComponents = {
  ClassTypeManagement,
  FlexibleClassAssignmentForm,
  StudentClassesDisplay,
  StudentFocusedAssignment,
  ClassFocusedAssignment,
  ClassEditingSection,
  DeleteClassEntirely,
  ClassProposalForm,
  ClassProposalManagement,
  useClassManagement
};

export default classManagementComponents;
